import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconEditProfile, Remove, Takephoto, Upload, Viewphoto } from '../../rootAccess';
import { showErrorToast } from '../../utils';
import CropperModal from '../Cropper';
import MenuButton from '../MenuButton';
import ProfileImage from '../ProfileImage';
import { WebCamera } from '../WebCamera';
import './DropdownImageUpload.scss';
import { setBodyClass } from '../../../Helpers/Chat/ChatHelper';

const DropdownImageUpload = ({ imageUri, onCapture, enableCrop = true, onRemove = () => {}, isEdit = false }) => {
    const fileRef = useRef(null);
    const [showCamera, setShowCamera] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [cropSrc, setCropSrc] = useState(null);
    const [resolvedSrc, setResolvedSrc] = useState(null);
    const [showViewer, setShowViewer] = useState(false);

    useEffect(() => {
        if (!imageUri) {
            setShowCropper(false);
            setShowViewer(false);
            setCropSrc(null);
            setResolvedSrc(null);
        }
    }, [imageUri]);

    const wrapperRef = useRef(null);

    const handleFileChange = async e => {
        const file = e.target.files[0];
        if (!file) return;
        const allowedImageTypes = ['image/png', 'image/jpeg'];
        if (e.target.value) {
            e.target.value = '';
        }
        if (!allowedImageTypes.includes(file.type)) {
            showErrorToast('Image type only allowed to upload');
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        if (enableCrop) {
            setCropSrc(objectUrl);
            setShowCropper(true);
        } else {
            onCapture(file);
        }
    };

    const handleFileUploadClick = () => {
        fileRef.current?.click();
    };

    const handleTakePhoto = () => {
        if (isEdit) {
            setBodyClass('edit_popup', false);
        }

        setShowCamera(true);
    };

    const handleCameraCapture = blob => {
        setShowCamera(false);
        if (enableCrop) {
            const objectUrl = URL.createObjectURL(blob);
            setCropSrc(objectUrl);
            setShowCropper(true);
        } else {
            onCapture(blob);
        }
    };

    const handleCloseCamera = () => {
        setShowCamera(false);
        if (isEdit) {
            setBodyClass('edit_popup', true);
        }
    };

    const onRemoveClick = () => {
        onRemove();
        setShowViewer(false);
    };

    const onCancel = () => {
        setShowCropper(false);
        setCropSrc(null);
        setShowViewer(false);
        if (isEdit) {
            setBodyClass('edit_popup', true);
        }
    };

    const handleViewPhoto = () => {
        if (isEdit) {
            setBodyClass('edit_popup', false);
        }
        if (cropSrc) {
            setShowViewer(true);
            return;
        }
        if (resolvedSrc) {
            setCropSrc(resolvedSrc);
            setShowViewer(true);
            return;
        }
        if (typeof imageUri === 'string') {
            setCropSrc(imageUri); // directly use the URL
            setShowViewer(true);
        } else if (imageUri instanceof Blob) {
            const objectUrl = URL.createObjectURL(imageUri);
            setCropSrc(objectUrl);
            setShowViewer(true);
        }
    };

    const handleUpdateImageSorce = image => {
        if (image) {
            const url = image instanceof Blob ? URL.createObjectURL(image) : image;
            setCropSrc(url);
            setResolvedSrc(url);
        }
    };

    const messageActionMenu = [
        imageUri && {
            icon: <Viewphoto style={{ width: '1em', height: '1em' }} />,
            label: 'View Photo',
            formatter: handleViewPhoto,
        },
        {
            icon: <Takephoto style={{ width: '1em', height: '1em', fill: 'none' }} />,
            label: 'Take Photo',
            formatter: handleTakePhoto,
        },
        {
            icon: <Upload style={{ width: '1em', height: '1em', fill: 'none' }} />,
            label: 'Upload Photo',
            formatter: handleFileUploadClick,
        },
        imageUri && {
            icon: <Remove style={{ width: '1em', height: '1em' }} />,
            label: 'Remove Photo',
            formatter: onRemoveClick,
        },
    ].filter(Boolean);

    return (
        <div ref={wrapperRef} className="dropdown-upload-wrapper">
            <div className="upload-trigger">
                <ProfileImage
                    imageUri={imageUri}
                    userId="createChannel"
                    alt="Channel Icon"
                    className="upload-preview"
                    setImageSource={handleUpdateImageSorce}
                />
                <div className="edit-icon">
                    <MenuButton
                        items={messageActionMenu}
                        trigger={
                            <i className="channel_uploadimg">
                                <IconEditProfile />
                            </i>
                        }
                        preferred="left"
                    />
                </div>
            </div>

            {showCamera && <WebCamera onCapture={handleCameraCapture} onClose={handleCloseCamera} />}

            {(showCropper || showViewer) && cropSrc && (
                <CropperModal
                    src={cropSrc}
                    onCrop={blob => {
                        setShowCropper(false);
                        onCapture(blob);
                    }}
                    viewOnly={showViewer}
                    onCancel={onCancel}
                    circularCrop={true}
                />
            )}

            <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
    );
};

DropdownImageUpload.propTypes = {
    imageUri: PropTypes.any,
    onCapture: PropTypes.func.isRequired,
    enableCrop: PropTypes.bool,
    onRemove: PropTypes.func,
};

export default DropdownImageUpload;
